import { CircularProgress, Grid } from '@material-ui/core';
import { Dialog, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { areaDetailsProperties, environmentConditions } from '../../graphql';
import { useMst } from '../../mobx-models/Root';
import { Discipline } from '../../models/models';
import type { AssetsByAreaHeirachy } from '../../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import type { MobileComponentLocationHeirarchy } from '../../rxdb/inspectionList/inspectionList.types';
import { isJestTest } from '../../test-helpers/isJestTest';
import {
  mockAllEnvironmentVariables,
  mockChildAreaDetails,
  mockParentAreaDetails,
} from '../../test-helpers/mockData/assetDetails';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { offlineTextMsg } from '../../utilities/constants';
import { AreaType, SiteLayer, SiteLayerId } from '../../utilities/enums';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import { downloadFile, getAreaType } from '../../utilities/utilityFunctions';
import RenderTreeView from '../AssetLocationTreeView';
import CustomButton from '../CustomButton';
import {
  AreaEnvironmentCondition,
  AreaRiskResult,
  EnvironmentCondition,
  EnvironmentConditionCategory,
  EnvironmentConditionObject,
  FileArea,
  HistoryAreaRiskResultInfo,
  SuperArea,
} from '../model/model';
import ShaftAreaProperties from '../ShaftAreaProperties';
import ShaftRiskAssessment from '../ShaftRiskAssessment';
import useStyles from './AssetLocationDetails.styles';

interface Props {
  assetLocationDialogTree: boolean;
  updateAssetLocationDialogStatus: (flag: boolean) => void;
  assetButton: string;
  assetLocationName: string;
  assetName: string;
  assetsByAreaHierarchyData: AssetsByAreaHeirachy[];
  areaListFromRxDB: MobileComponentLocationHeirarchy[];
}

const AssetLocationDetails = ({
  assetLocationDialogTree,
  assetLocationName,
  updateAssetLocationDialogStatus,
  assetButton,
  assetName,
  assetsByAreaHierarchyData,
  areaListFromRxDB,
}: Props) => {
  const size = useWindowDimensions();
  const classes = useStyles({ size });
  const { SiteDiscipline } = useMst();
  const isShaft = SiteDiscipline.disciplineId === Discipline.Shaft;
  const online = useOnlineStatus();
  const [areaType, setareaType] = useState<string>('');
  const [areaProperties, setareaProperties] = useState<SuperArea>({} as SuperArea);
  const [parentName, setParentName] = useState<string>('');
  const [dynamicSelectedLocation, setDynamicSelectedLocation] = useState<number>(0);
  const [environmentConditionMasterData, setEnvironmentConditionMasterData] = useState<
    EnvironmentConditionCategory[]
  >([]);
  const [envPayload, setEnvPayload] = useState<EnvironmentConditionObject>(
    {} as EnvironmentConditionObject,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [shaftRiskAssessmentData, setShaftRiskAssessmentData] = useState<
    HistoryAreaRiskResultInfo[]
  >([] as HistoryAreaRiskResultInfo[]);

  const fetchAllData = async () => {
    setIsLoading(true);
    let result;
    try {
      result = isJestTest()
        ? mockChildAreaDetails
        : await areaDetailsProperties(dynamicSelectedLocation);
    } catch (err) {
      setIsLoading(false);
    }
    if (result) {
      const allData = result.data.areaDetails;
      setareaProperties(allData);
      const areaTypes = getAreaType(isShaft, allData?.area.fkAreaType);

      if (isShaft) {
        const riskData = allData.area.areaRiskResult;
        const historyRiskData = allData.historyAreaRiskResult;
        const riskAssessmentData = riskData?.map((risk: AreaRiskResult) => {
          return historyRiskData?.find(
            (historyRisk: HistoryAreaRiskResultInfo) =>
              historyRisk.fkAreaRiskCategoryRankType === risk.fkAreaRiskCategoryRankType &&
              historyRisk.fkRiskAssessmentCategory === risk.fkRiskAssessmentCategory &&
              historyRisk.comment === risk.comment,
          );
        });
        if (riskAssessmentData) {
          setShaftRiskAssessmentData(
            riskAssessmentData.filter((risk: HistoryAreaRiskResultInfo) => risk),
          );
        }
      }

      setareaType(areaTypes);
      if (allData?.area.fkParentArea) {
        const parentAreaLocation = isJestTest()
          ? mockParentAreaDetails
          : await areaDetailsProperties(allData.area.fkParentArea);
        setParentName(parentAreaLocation.data.areaDetails.area.name);
      } else {
        if (allData?.area.siteLayer === SiteLayerId.Surface) {
          setParentName(SiteLayer.Surface);
        } else if (allData?.area.siteLayer === SiteLayerId.Shaft) {
          setParentName(SiteLayer.Shaft);
        } else {
          setParentName(SiteLayer.UnderGround);
        }
      }
    }
    setIsLoading(false);
  };

  const getEnvironmentConditions = async () => {
    const result = isJestTest() ? mockAllEnvironmentVariables : await environmentConditions();
    const environmentData = result.data.allEnvironmentConditions;
    setEnvironmentConditionMasterData(environmentData);
  };

  const updateOnChange = async (val: number) => {
    if (![1, 2].includes(val)) {
      setDynamicSelectedLocation(val);
      localStorage.setItem('id', String(val));
    }
  };

  const LayoutFields = (props: { label: string; defaultValue: string }) => {
    const { label, defaultValue } = props;
    let prefix = label?.toLowerCase();
    if (prefix.includes('upper')) {
      prefix = 'upper';
    } else if (prefix.includes('lower')) {
      prefix = 'lower';
    }
    const westEast = ['west', 'east'].includes(prefix);
    return (
      <Grid className={westEast ? classes.westEastBlock : classes.northSouthBlock}>
        <Grid className={westEast ? classes.westEastBlockHeight : classes.northSouthblockHeight}>
          <p> {label}</p>
        </Grid>
        <Grid className={classes.blockShape}>
          <p className={classes.textStyles}>
            <label>{defaultValue}</label>
          </p>
        </Grid>
      </Grid>
    );
  };

  const onClose = () => {
    updateAssetLocationDialogStatus(false);
  };

  useEffect(() => {
    getEnvironmentConditions();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [dynamicSelectedLocation]);

  useEffect(() => {
    if (
      areaProperties?.area?.areaEnvironmentCondition?.length > 0 &&
      environmentConditionMasterData?.length > 0
    ) {
      const data: EnvironmentConditionObject = {} as EnvironmentConditionObject;
      areaProperties.area.areaEnvironmentCondition.map((item: AreaEnvironmentCondition) => {
        if (item.fkEnvironmentCondition) {
          const tempEnv = environmentConditionMasterData?.find(
            (val: EnvironmentConditionCategory) =>
              item.name?.toLowerCase() === val.name?.toLowerCase(),
          );
          if (tempEnv) {
            const result = tempEnv.environmentCondition?.find(
              (val: EnvironmentCondition) =>
                val.pkEnvironmentCondition === item.fkEnvironmentCondition,
            );
            if (result) data[item.name as keyof EnvironmentConditionObject] = result;
          }
        }
      });
      setEnvPayload(data);
    }
  }, [areaProperties, environmentConditionMasterData]);

  return (
    <div className={classes.scrollHide}>
      <Dialog
        open={assetLocationDialogTree}
        titleText={`${isShaft ? AreaType.SubArea : AreaType.Asset} Location Details`}
        onClose={onClose}
        backButton={false}
        dialogActions={
          <CustomButton buttonText="Close" onClose={onClose} dataTestId="close-treeview-dialog" />
        }
        fullScreen
        maxWidth="xs"
        noMinHeight
      >
        <Grid className={classes.mainGrid}>
          <Grid item container xs={6} className={`${classes?.root} ${classes.treeviewGrid}`}>
            <RenderTreeView
              onChange={updateOnChange}
              assetButton={assetButton}
              assetLocationName={assetLocationName}
              assetName={assetName}
              assetsByAreaHierarchyData={assetsByAreaHierarchyData}
              areaListFromRxDB={areaListFromRxDB}
            />
          </Grid>

          {online && !isLoading ? (
            <Grid item container xs={6} className={classes.root}>
              <Grid container xs={12} className={classes.areaPropertiesMainGrid}>
                {/*Area Properties part */}

                <Grid container xs={6} className={classes.areaPropertiesChildGrid}>
                  <Typography variant="h5" className={classes.title}>
                    {`${areaType === AreaType.MainArea ? 'Area' : areaType} Properties`}
                  </Typography>
                  <Grid xs={12} className={classes.properties}>
                    {isShaft ? (
                      <ShaftAreaProperties
                        areaType={areaType}
                        parentName={parentName}
                        areaProperties={areaProperties?.area}
                        assetCategory={{
                          assetMainCategory: areaProperties?.assetMainCategoryName,
                          assetSubCategory: areaProperties?.assetCategoryName,
                        }}
                      />
                    ) : (
                      <>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                {`Parent Area`}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1" fontWeight="normal">
                                {parentName || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                Area Type
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1" fontWeight="normal">
                                {areaType || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                Area Name
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1" fontWeight="normal">
                                {areaProperties.area.name || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                {(areaType ? areaType : 'Main Area') + ' ' + ' ID'}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1" fontWeight="normal">
                                {areaProperties.area.pcsId || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                {(areaType ? areaType : 'Main Area') + ' Layout'}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              {areaProperties?.area?.fileArea?.length
                                ? areaProperties?.area?.fileArea?.map((file: FileArea) => {
                                    return (
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight="normal"
                                        key={file.fkFileAims}
                                        onClick={() => downloadFile(file.fkFileAims)}
                                      >
                                        {file.name}
                                      </Typography>
                                    );
                                  })
                                : '-'}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                Floor Level
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1" fontWeight="normal">
                                {areaProperties.area.floorLevel || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                Lower Bound
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1" fontWeight="normal">
                                {areaProperties.area.lowBound || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} className={classes.assetProperties}>
                          <Grid item xs={12} className={classes.innerBlock}>
                            <Grid item xs={5}>
                              <Typography
                                variant="subtitle1"
                                color="textSecondary"
                                fontWeight="normal"
                              >
                                Upper Bound
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1" fontWeight="normal">
                                {areaProperties.area.upperBound || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {Object.keys(envPayload).map((property: string, index: number) => {
                      return (
                        envPayload[property as keyof EnvironmentConditionObject].name && (
                          <Grid
                            item
                            container
                            xs={12}
                            className={classes.assetProperties}
                            key={index}
                          >
                            <Grid item xs={12} className={classes.innerBlock}>
                              <Grid item xs={5}>
                                <Typography
                                  variant="subtitle1"
                                  color="textSecondary"
                                  fontWeight="normal"
                                >
                                  {property.charAt(0).toUpperCase() + property.slice(1)}
                                </Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="subtitle1" fontWeight="normal">
                                  {envPayload[property as keyof EnvironmentConditionObject].name}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        )
                      );
                    })}

                    <Grid item container xs={12} className={classes.assetProperties}>
                      <Grid item xs={12} className={classes.innerBlock}>
                        <Grid item xs={5}>
                          <Typography variant="subtitle1" color="textSecondary" fontWeight="normal">
                            Comments
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="subtitle1" fontWeight="normal">
                            {areaProperties.area.comment || '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {areaType === AreaType.CubeArea && (
                  <Grid container className={classes.gridlines}>
                    <Grid className={classes.gridLineTag}>
                      <h5>* Gridlines</h5>
                    </Grid>

                    <Grid className={classes.layout}>
                      <Grid className={classes.gridLinesLayout}>
                        <LayoutFields
                          label="West"
                          defaultValue={areaProperties.area.westGridLine}
                        />
                        <LayoutFields
                          label="East"
                          defaultValue={areaProperties.area.eastGridLine}
                        />
                      </Grid>
                      <Grid className={`north-south-block ${classes.northSouth}`}>
                        <LayoutFields
                          label="North"
                          defaultValue={areaProperties.area.northGridLine}
                        />
                        <LayoutFields
                          label="South"
                          defaultValue={areaProperties.area.southGridLine}
                        />
                      </Grid>
                      <Grid className={`sub-area-main ${classes.subArea}`}>
                        <Grid className={`sub-area ${classes.subAreaElevation}`}>
                          <Grid className={`subarea-top ${classes.subAreaGrid}`}> </Grid>
                        </Grid>
                        <Grid className={`sub-area ${classes.subAreaElevation}`}>
                          <Grid className={`subarea-left ${classes.subAreaGrid}`}> </Grid>
                          <Grid
                            className={`subarea-line ${classes.subAreaGrid} ${classes.linePosition}`}
                          >
                            <p className={classes.areaType}>{areaType}</p>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid className={`error ${classes.error}`} />
                  </Grid>
                )}
                {areaType === AreaType.Elevation && (
                  <Grid
                    item
                    container
                    className={`${classes.gridlines} ${classes.gridlinesElevation}`}
                  >
                    <Grid className={classes.gridLineTag}>
                      <h5>* Gridlines</h5>
                    </Grid>

                    <div className={classes.graphMainElevation}>
                      <div className={classes.shaftUpperLowerBlock}>
                        <LayoutFields
                          label="Upper Elevation (ft)"
                          defaultValue={areaProperties.area.upperGridLine}
                        />
                        <LayoutFields
                          label="Lower Elevation (ft)"
                          defaultValue={areaProperties.area.lowerGridLine}
                        />
                      </div>
                      <div className={classes.subAreaMainElevation}>
                        <div className={classes.subAreaElevation}>
                          <div className={`${classes.subAreaElevation2} ${classes.left}`}>
                            <div
                              className={`${classes.subAreaElevation2} ${classes.left} ${classes.middle}`}
                            />
                          </div>
                          <div className={classes.subareaLineElevation}>
                            <p className={classes.elevationP}>{areaType}</p>
                          </div>
                          <div className={`${classes.subAreaElevation2} ${classes.right}`} />
                        </div>
                      </div>
                    </div>
                  </Grid>
                )}

                {/*Risk Assessment part */}
                <Grid xs={12} className={classes.risk}>
                  <Typography variant="h5" className={classes.riskText}>
                    Risk Assessment
                  </Typography>

                  {isShaft ? (
                    <ShaftRiskAssessment riskAssessmentData={shaftRiskAssessmentData} />
                  ) : (
                    <>
                      <Grid
                        item
                        container
                        xs={12}
                        className={`${classes.assetProperties} ${classes.topPadding}`}
                      >
                        <Grid item xs={12} className={classes.innerBlock}>
                          <Grid item xs={5}>
                            <Typography
                              variant="subtitle1"
                              color="textSecondary"
                              fontWeight="normal"
                            >
                              Category
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography variant="subtitle1" fontWeight="normal">
                              {areaProperties?.historyAreaRiskResult &&
                              areaProperties?.historyAreaRiskResult[0]?.category
                                ? areaProperties?.historyAreaRiskResult[0]?.category
                                : '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item container xs={12} className={classes.assetProperties}>
                        <Grid item xs={12} className={classes.innerBlock}>
                          <Grid item xs={5}>
                            <Typography
                              variant="subtitle1"
                              color="textSecondary"
                              fontWeight="normal"
                            >
                              Assessed Value
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography variant="subtitle1" fontWeight="normal">
                              {areaProperties?.historyAreaRiskResult &&
                              areaProperties?.historyAreaRiskResult[0]?.riskRankValue &&
                              areaProperties?.historyAreaRiskResult[0]?.riskRankName
                                ? `${areaProperties?.historyAreaRiskResult[0]?.riskRankValue} - ${areaProperties?.historyAreaRiskResult[0]?.riskRankName}`
                                : '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item container xs={12} className={classes.assetProperties}>
                        <Grid item xs={12} className={classes.innerBlock}>
                          <Grid item xs={5}>
                            <Typography
                              variant="subtitle1"
                              color="textSecondary"
                              fontWeight="normal"
                            >
                              Assessment Category
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography variant="subtitle1" fontWeight="normal">
                              {areaProperties?.historyAreaRiskResult &&
                              areaProperties?.historyAreaRiskResult[0]?.riskAssessmentCategoryName
                                ? areaProperties?.historyAreaRiskResult[0]
                                    ?.riskAssessmentCategoryName
                                : '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item container xs={12} className={classes.assetProperties}>
                        <Grid item xs={12} className={classes.innerBlock}>
                          <Grid item xs={5}>
                            <Typography
                              variant="subtitle1"
                              color="textSecondary"
                              fontWeight="normal"
                            >
                              Comments
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography variant="subtitle1" fontWeight="normal">
                              {areaProperties?.historyAreaRiskResult &&
                              areaProperties?.historyAreaRiskResult[0]?.comment
                                ? areaProperties?.historyAreaRiskResult[0]?.comment
                                : '-'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid
              item
              container
              xs={6}
              alignContent="center"
              justifyContent="center"
              className={`${classes.root} ${classes.loadingOrError}`}
            >
              {!online ? offlineTextMsg : <CircularProgress color="primary" />}
            </Grid>
          )}
        </Grid>
      </Dialog>
    </div>
  );
};

export default observer(AssetLocationDetails);
